//src/components/Dialogs/TemplateWelcomeModal.jsx

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Slide,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RuleIcon from '@mui/icons-material/Rule';
import TuneIcon from '@mui/icons-material/Tune';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * TemplateWelcomeModal
 * First run welcome dialog after a template is duplicated.
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - onPrimary: () => void
 * - templateLabel?: string
 */
const TemplateWelcomeModal = ({
  open,
  onClose,
  onPrimary,
  templateLabel = 'Your template',
}) => {
  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      aria-describedby="template-welcome-description"
      fullWidth
      maxWidth="sm"
PaperProps={{
  elevation: 0,
  sx: {
    borderRadius: 3,
    border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: '0 18px 50px rgba(0,0,0,0.14)',
  },
}}
    >
<DialogTitle
  sx={{
    m: 0,
    px: 3,
    pt: 3,
    pb: 2,
    position: 'relative', // ✅ anchor for absolute close button
  }}
>
  {/* Close pinned to the modal header corner */}
  <IconButton
    aria-label="close"
    onClick={onClose}
    sx={{
      position: 'absolute',
      right: 12,
      top: 12,
      color: (theme) => theme.palette.grey[500],
    }}
  >
    <CloseIcon />
  </IconButton>

  {/* Header content */}
  <Box sx={{ pr: 5 /* ✅ keeps text from running under close icon */ }}>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 800,
        letterSpacing: '-0.01em',
        lineHeight: 1.2,
        mb: 0.75,
      }}
    >
      Your {templateLabel} is ready
    </Typography>

    <Typography
      id="template-welcome-description"
      variant="body2"
      sx={{
        color: 'text.secondary',
        fontWeight: 500,
        lineHeight: 1.5,
        maxWidth: 520,
      }}
    >
      Make a few quick adjustments, then publish your governed campaign link.
    </Typography>
  </Box>
</DialogTitle>

<DialogContent sx={{ px: 3, pb: 2.5, pt: 0 }}>
<Typography
  variant="overline"
  sx={{
    display: 'block',
    color: 'text.secondary',
    fontWeight: 700,
    letterSpacing: '0.08em',
    mb: 1.25,
  }}
>
  3 quick steps
</Typography>

  {/* Step cards (scan-friendly, less “text wall”) */}
  <Box sx={{ display: 'grid', gap: 1.25 }}>
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid rgba(0,0,0,0.06)',
        backgroundColor: 'rgba(0,0,0,0.01)',
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          flexShrink: 0,
        }}
      >
        <RuleIcon fontSize="small" />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, lineHeight: 1.25 }}>
          Confirm your tracking structure
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', lineHeight: 1.45, mt: 0.25 }}
        >
          Review parameter names, delimiters, and formatting rules to keep your analytics reporting clean.
        </Typography>
      </Box>
    </Box>

    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid rgba(0,0,0,0.06)',
        backgroundColor: 'rgba(0,0,0,0.01)',
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          flexShrink: 0,
        }}
      >
        <TuneIcon fontSize="small" />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, lineHeight: 1.25 }}>
          Customize dropdown values
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', lineHeight: 1.45, mt: 0.25 }}
        >
          Update source, medium, campaign, and other values to match your team’s standards.
        </Typography>
      </Box>
    </Box>

    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid rgba(0,0,0,0.06)',
        backgroundColor: 'rgba(0,0,0,0.01)',
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          flexShrink: 0,
        }}
      >
        <RocketLaunchIcon fontSize="small" />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, lineHeight: 1.25 }}>
          Set your publish behavior
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', lineHeight: 1.45, mt: 0.25 }}
        >
          Choose what happens after submit and generate a shareable campaign builder link.
        </Typography>
      </Box>
    </Box>
  </Box>
</DialogContent>

<DialogActions
  sx={{
    px: 3,
    pb: 2.5,
    pt: 1.5,
    gap: 1,
  }}
>
<Button
  onClick={onClose}
  color="inherit"
  sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'none' }}
>
  Not now
</Button>

  <Button
    variant="contained"
    onClick={onPrimary}
    autoFocus
    sx={{
      borderRadius: 2,
      px: 2.25,
      py: 1.1,
      fontWeight: 700,
      textTransform: 'none',
    }}
  >
    Continue to builder
  </Button>
</DialogActions>
    </Dialog>
  );
};

export default TemplateWelcomeModal;